import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import type maplibregl from "maplibre-gl";
import { type StyleSpecification } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import "@maplibre/maplibre-gl-leaflet";
import L from "leaflet";
import { LAYER_Z_INDEX } from "@/features/map/constants/layerZIndex";

// MapLibre レイヤーの デフォルト設定
const MAPLIBRE_MIN_ZOOM = 0;
const MAPLIBRE_MAX_ZOOM = 28;
const MAPLIBRE_SCROLL_ZOOM_DISABLED = false;

// 子コンポーネントが addSource/addLayer でソースとレイヤーを追加する
const style: StyleSpecification = {
  version: 8,
  sources: {},
  layers: [],
};

const options = {
  interactive: true,
  minZoom: MAPLIBRE_MIN_ZOOM,
  maxZoom: MAPLIBRE_MAX_ZOOM,
  scrollZoom: MAPLIBRE_SCROLL_ZOOM_DISABLED,
  style,
  canvasContextAttributes: {
    preserveDrawingBuffer: true,
  },
};

interface MaplibreContextType {
  layer: L.MaplibreGL | null;
  maplibreMap: maplibregl.Map | null;
}

/**
 * アプリケーション内で MapLibre 固有の状態や参照を管理するための React Context。
 *
 * MaplibreLayerContext配下のコンポーネントでuseMaplibreContext()を呼び出すことで読み出しができます
 */
const MaplibreContext = createContext<MaplibreContextType>({
  layer: null,
  maplibreMap: null,
});

export const useMaplibreContext = (): MaplibreContextType => {
  return useContext(MaplibreContext);
};

interface Props {
  children: ReactNode;
  zIndex?: number;
}

/**
 * MapLibre インスタンスを単一に管理し、Context で共有するプロバイダー
 *
 * Leaflet マップ上に L.maplibreGL レイヤーを作成して Leaflet に統合し、
 * 内部の MapLibre インスタンスを useMaplibreContext で子コンポーネントに提供する
 */
export const MaplibreLayerContext: React.FC<Props> = ({
  children,
  zIndex = LAYER_Z_INDEX.MAPLIBRE,
}) => {
  const map = useMap();

  // maplibreのレイヤーインスタンスを初期化
  const layerRef = useRef<L.MaplibreGL | null>(null);
  if (!layerRef.current) {
    layerRef.current = L.maplibreGL(options);
  }

  const layer = layerRef.current;
  const [maplibreMap, setMaplibreMap] = useState<maplibregl.Map | null>(null);

  /**
   * コンポーネント削除時、maplibreインスタンスを破棄する
   */
  useEffect(() => {
    return () => {
      layer.remove();
    };
  }, [layer]);

  /**
   * leafletのmapオブジェクト初期化後にmaplibreのレイヤーを追加する処理
   */
  useEffect(() => {
    if (!map || !layer) return;

    // vectorTileLayerがすでに追加済なら何もしない
    if (map.hasLayer(layer)) {
      return;
    }

    // 事前に生成したmaplibreのインスタンスをleafletレイヤーとして追加
    layer.addTo(map);

    // MapLibre インスタンスを取得
    const newMaplibreMap = layer.getMaplibreMap();
    setMaplibreMap(newMaplibreMap);

    // ドラッグで Leaflet マップが移動しないようにする
    newMaplibreMap.dragPan.disable();

    // Leaflet MapLibre GL ラッパーの制限回避
    // _update() 呼び出しで MapLibre レイヤーの描画を強制的に更新
    // 詳細: https://www.notion.so/trustart/34391683363346e5ba5d4758241f1ccd?pvs=4
    (layer as L.MaplibreGL & { _update: () => void })._update();

    // z-index を設定（MapLibre キャンバスの層位置を指定）
    // maplibreレイヤーはできれば下の方に配置したい、この値は基本的に99で作ります
    layer.getContainer().style.zIndex = String(zIndex);
  }, [map, layer, zIndex]);

  const contextValue = useMemo(
    () => ({
      layer,
      maplibreMap,
    }),
    [layer, maplibreMap]
  );

  return (
    <MaplibreContext.Provider value={contextValue}>
      {children}
    </MaplibreContext.Provider>
  );
};
